import { useState, useCallback, useRef } from "react";
import { getSandboxClient, TestConfiguration } from "sj-ui-toolkit/src/client/svcClients";
import type { ReplParam, TestStatus as TestCodeResult } from "sj-ui-toolkit/src/client/svcClients";

// This is a subset of the full TestStatus returned by the sandbox client
export interface TestStatus {
  status?: TestCodeResult["status"]; //  "incomplete" | "passed" | "failed" | "error" | "timeout"
  progress?: TestCodeResult["progress"]; // "submitted" | "blocked" | "running" | "complete"
  results?: TestCodeResult["results"];
}

export interface UseTestingResult {
  isTestRunning: boolean;
  requestError: Error | null;
  testStatus: TestStatus | null;
  runTests: () => Promise<{ runId: string } | null>;
}

/**
 * Hook to manage test execution and status polling for a sandbox lesson.
 *
 * @returns Object containing test running state, error state, and testCode function
 */
export const useTestingInternal = ({
  readyTimestamp,
  tests,
  tenantName,
  tournamentUuid,
  replParam,
  handlePollResponse,
}: {
  readyTimestamp: Date | null;
  tests?: TestConfiguration;
  tenantName?: string;
  tournamentUuid?: string;
  replParam?: ReplParam;
  handlePollResponse?: (data: TestCodeResult) => void;
}): UseTestingResult => {
  const sandboxClient = getSandboxClient();
  const [isTestRunning, setIsTestRunning] = useState<boolean>(false);
  const [requestError, setRequestError] = useState<Error | null>(null);
  const [testStatus, setTestStatus] = useState<TestStatus | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  /**
   * Poll for test status using checkStatus
   */
  const pollTestStatus = useCallback(
    async (runId: string): Promise<boolean> => {
      try {
        const call = sandboxClient.checkStatus(runId);
        const data = await call.promise;

        if (!data) {
          throw new Error("No data returned from test status check");
        }

        // Call handler if provided
        if (handlePollResponse) {
          handlePollResponse(data);
        }

        // Update test status state
        setTestStatus({
          status: data.status,
          progress: data.progress,
          results: data.results,
        });

        return data.status !== "incomplete";
      } catch (err) {
        console.error("Error polling test status:", err);
        const errorObject = err instanceof Error ? err : new Error(String(err));
        setRequestError(errorObject);
        return true; // Stop polling on error
      }
    },
    [sandboxClient, handlePollResponse]
  );

  /**
   * Start polling for test completion
   */
  const startPolling = useCallback(
    (runId: string) => {
      // Clear any existing polling interval
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }

      // Do an immediate poll
      pollTestStatus(runId).then(isComplete => {
        if (isComplete) {
          setIsTestRunning(false);
          return;
        }

        // Then poll every 2 seconds
        pollingIntervalRef.current = setInterval(async () => {
          const isComplete = await pollTestStatus(runId);
          if (isComplete) {
            setIsTestRunning(false);
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
          }
        }, 2000);
      });
    },
    [pollTestStatus]
  );

  /**
   * Generic runTests method that delegates to testCode or testSandbox
   * based on tests.kind property
   */
  const runTests = useCallback(async (): Promise<{ runId: string } | null> => {
    if (!tests || tests.kind === "None") {
      console.warn("No tests configured to run.");
      return null;
    }

    // Set up to run tests
    setIsTestRunning(true);
    setRequestError(null);

    const lessonContext = {
      tenantName,
      tournamentUuid: tournamentUuid,
      sessionStart: readyTimestamp ? readyTimestamp.toISOString() : undefined,
    };

    let response = { runId: "" };
    let call;

    // Call the appropriate sandboxClient methods for the tests type
    if (tests?.kind === "VerifySandbox") {
      call = sandboxClient.testSandbox(lessonContext);
    } else {
      // Otherwise, get code from the sandbox and send it for testing
      const code = await sandboxClient.getCode().promise;
      call = sandboxClient.testCode(code, lessonContext, undefined, replParam); // 3rd param is for devTags
    }

    try {
      response = await call.promise;
      if (!response?.runId) {
        throw new Error("No runId returned from test execution");
      }

      // Start polling for test results
      startPolling(response.runId);

      // Return the runId
      return { runId: response.runId };
    } catch (err) {
      console.error("Error executing test code:", err);
      const errorObject = err instanceof Error ? err : new Error(String(err));
      setRequestError(errorObject);
      setIsTestRunning(false);
      return null;
    }
  }, [tests?.kind, tenantName, tournamentUuid, readyTimestamp, replParam]);

  return {
    isTestRunning,
    requestError,
    testStatus,
    runTests,
  };
};
